import { useEffect, useMemo, useState } from "react";
import { RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  describeCron,
  diffLines,
  digest,
  hmac,
  nextCronRuns,
  relativeTime,
  renderMarkdown,
  textStats,
} from "@/lib/utilfns";
import { Card } from "@/components/ui/primitives";
import { CopyButton, OutputPane, inputCls, labelCls, textareaCls } from "./engines";

/* ── Regex tester ─────────────────────────────────────────────────────────── */

export function RegexTester() {
  const [pattern, setPattern] = useState("\\b\\w+@\\w+\\.\\w+\\b");
  const [flags, setFlags] = useState("g");
  const [text, setText] = useState("Reach us at ops@hive.dev or oncall@momentum.ai — not at.this");

  const result = useMemo(() => {
    if (!pattern) return { parts: [{ match: false, text }], matches: [], error: null as string | null };
    let re: RegExp;
    try {
      re = new RegExp(pattern, flags.includes("g") ? flags : flags + "g");
    } catch (e) {
      return { parts: [{ match: false, text }], matches: [], error: (e as Error).message };
    }
    const parts: { match: boolean; text: string }[] = [];
    const matches: string[] = [];
    let last = 0;
    let m: RegExpExecArray | null;
    let guard = 0;
    while ((m = re.exec(text)) && guard++ < 5000) {
      if (m.index > last) parts.push({ match: false, text: text.slice(last, m.index) });
      parts.push({ match: true, text: m[0] });
      matches.push(m[0] + (m.length > 1 ? `   groups: ${m.slice(1).map((g) => g ?? "∅").join(" · ")}` : ""));
      last = m.index + m[0].length;
      if (m[0] === "") re.lastIndex++;
    }
    if (last < text.length) parts.push({ match: false, text: text.slice(last) });
    return { parts, matches, error: null };
  }, [pattern, flags, text]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <label className="block flex-1">
          <span className={labelCls}>Pattern</span>
          <input
            value={pattern}
            onChange={(e) => setPattern(e.target.value)}
            className={cn(inputCls, "font-mono")}
            spellCheck={false}
          />
        </label>
        <label className="block w-24">
          <span className={labelCls}>Flags</span>
          <input
            value={flags}
            onChange={(e) => setFlags(e.target.value.replace(/[^gimsuy]/g, ""))}
            className={cn(inputCls, "font-mono")}
          />
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>Test string</span>
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          className={textareaCls}
          spellCheck={false}
        />
      </label>
      {result.error ? (
        <Card className="p-3">
          <div className="font-mono text-xs text-rose-400">{result.error}</div>
        </Card>
      ) : (
        <>
          <div>
            <span className={labelCls}>
              Highlighted · {result.matches.length} match{result.matches.length === 1 ? "" : "es"}
            </span>
            <div className="whitespace-pre-wrap break-words rounded-lg border border-app bg-surface-2 px-3 py-2 font-mono text-xs leading-relaxed text-app">
              {result.parts.map((p, i) =>
                p.match ? (
                  <mark key={i} className="rounded bg-amber-400/30 px-0.5 text-app">
                    {p.text}
                  </mark>
                ) : (
                  <span key={i}>{p.text}</span>
                )
              )}
            </div>
          </div>
          {result.matches.length > 0 && (
            <OutputPane text={result.matches.join("\n")} minHeight={80} />
          )}
        </>
      )}
    </div>
  );
}

/* ── Diff viewer ──────────────────────────────────────────────────────────── */

export function DiffViewer() {
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const diff = useMemo(() => {
    if (!a && !b) return { rows: [], error: null as string | null };
    try {
      return { rows: diffLines(a, b), error: null };
    } catch (e) {
      return { rows: [], error: (e as Error).message };
    }
  }, [a, b]);
  const added = diff.rows.filter((r) => r.type === "add").length;
  const removed = diff.rows.filter((r) => r.type === "del").length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className={labelCls}>Original</span>
          <textarea value={a} onChange={(e) => setA(e.target.value)} className={textareaCls} spellCheck={false} />
        </label>
        <label className="block">
          <span className={labelCls}>Changed</span>
          <textarea value={b} onChange={(e) => setB(e.target.value)} className={textareaCls} spellCheck={false} />
        </label>
      </div>
      {diff.error ? (
        <Card className="p-3">
          <div className="font-mono text-xs text-rose-400">{diff.error}</div>
        </Card>
      ) : diff.rows.length > 0 ? (
        <div>
          <span className={labelCls}>
            Diff · <span className="text-emerald-500">+{added}</span>{" "}
            <span className="text-rose-400">−{removed}</span>
          </span>
          <div className="max-h-[360px] overflow-auto rounded-lg border border-app bg-surface-2 font-mono text-xs leading-relaxed">
            {diff.rows.map((r, i) => (
              <div
                key={i}
                className={cn(
                  "flex gap-2 whitespace-pre-wrap break-all border-l-2 px-3 py-0.5",
                  r.type === "add" && "border-emerald-500 bg-emerald-500/10 text-emerald-400",
                  r.type === "del" && "border-rose-500 bg-rose-500/10 text-rose-400",
                  r.type === "same" && "border-transparent text-app-1"
                )}
              >
                <span className="w-3 shrink-0 select-none text-app-2">
                  {r.type === "add" ? "+" : r.type === "del" ? "−" : " "}
                </span>
                {r.text || " "}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Find & replace ───────────────────────────────────────────────────────── */

export function FindReplace() {
  const [find, setFind] = useState("");
  const [replace, setReplace] = useState("");
  const [useRegex, setUseRegex] = useState(false);
  const [input, setInput] = useState("");

  const result = useMemo(() => {
    if (!input || !find) return { text: input, count: 0, error: null as string | null };
    try {
      const re = useRegex
        ? new RegExp(find, "g")
        : new RegExp(find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "g");
      const count = (input.match(re) ?? []).length;
      return { text: input.replace(re, replace), count, error: null };
    } catch (e) {
      return { text: "", count: 0, error: (e as Error).message };
    }
  }, [input, find, replace, useRegex]);

  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <label className="block flex-1">
          <span className={labelCls}>Find</span>
          <input value={find} onChange={(e) => setFind(e.target.value)} className={cn(inputCls, "font-mono")} />
        </label>
        <label className="block flex-1">
          <span className={labelCls}>Replace with</span>
          <input value={replace} onChange={(e) => setReplace(e.target.value)} className={cn(inputCls, "font-mono")} />
        </label>
        <label className="flex h-[34px] items-center gap-2 text-xs text-app-1">
          <input
            type="checkbox"
            checked={useRegex}
            onChange={(e) => setUseRegex(e.target.checked)}
            className="h-4 w-4 accent-amber-500"
          />
          Regex
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>Input</span>
        <textarea value={input} onChange={(e) => setInput(e.target.value)} className={textareaCls} spellCheck={false} />
      </label>
      <div>
        <span className={labelCls}>{result.count} replacement{result.count === 1 ? "" : "s"}</span>
        <OutputPane text={result.text} error={result.error} />
      </div>
    </div>
  );
}

/* ── Word counter ─────────────────────────────────────────────────────────── */

export function WordCounter() {
  const [input, setInput] = useState("");
  const s = textStats(input);
  const tiles: [string, string][] = [
    ["Words", s.words.toLocaleString()],
    ["Characters", s.chars.toLocaleString()],
    ["No spaces", s.charsNoSpace.toLocaleString()],
    ["Lines", s.lines.toLocaleString()],
    ["Sentences", s.sentences.toLocaleString()],
    ["Paragraphs", s.paragraphs.toLocaleString()],
    ["Read time", input.trim() ? `~${s.readMinutes} min` : "—"],
  ];
  return (
    <div className="space-y-4">
      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Paste text to analyze…"
        className={cn(textareaCls, "min-h-[180px]")}
      />
      <div className="grid grid-cols-4 gap-3">
        {tiles.map(([label, value]) => (
          <Card key={label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-app-2">{label}</div>
            <div className="mt-0.5 text-lg font-semibold text-app tabular-nums">{value}</div>
          </Card>
        ))}
      </div>
    </div>
  );
}

/* ── Markdown / HTML previews ─────────────────────────────────────────────── */

const MD_SAMPLE = `# Release checklist

A **quick** preview of _markdown_ rendering.

- Verify the \`hooks\` window
- Ship the launcher
- [Docs](https://example.com)

> Everything is a hook-triggered chain.

\`\`\`
npm run build
\`\`\``;

export function MarkdownPreview() {
  const [input, setInput] = useState(MD_SAMPLE);
  const html = useMemo(() => renderMarkdown(input), [input]);
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="block">
        <span className={labelCls}>Markdown</span>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className={cn(textareaCls, "min-h-[340px]")}
          spellCheck={false}
        />
      </label>
      <div>
        <span className={labelCls}>Preview</span>
        <div
          className="markdown-preview min-h-[340px] overflow-auto rounded-lg border border-app bg-surface-2 px-4 py-3 text-sm leading-relaxed text-app [&_a]:text-sky-400 [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-app [&_blockquote]:pl-3 [&_blockquote]:text-app-2 [&_code]:rounded [&_code]:bg-app/10 [&_code]:px-1 [&_code]:font-mono [&_code]:text-[0.85em] [&_h1]:text-xl [&_h1]:font-semibold [&_h2]:text-lg [&_h2]:font-semibold [&_h3]:text-base [&_h3]:font-semibold [&_hr]:my-3 [&_hr]:border-app [&_li]:ml-4 [&_ol]:list-decimal [&_p]:my-2 [&_pre]:my-2 [&_pre]:overflow-auto [&_pre]:rounded-lg [&_pre]:bg-app/10 [&_pre]:p-3 [&_ul]:list-disc"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      </div>
    </div>
  );
}

export function HtmlPreview() {
  const [input, setInput] = useState("<h1>Hello Hive</h1>\n<p>Rendered in a sandboxed iframe.</p>");
  return (
    <div className="grid grid-cols-2 gap-3">
      <label className="block">
        <span className={labelCls}>HTML</span>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className={cn(textareaCls, "min-h-[340px]")}
          spellCheck={false}
        />
      </label>
      <div>
        <span className={labelCls}>Preview (sandboxed — no scripts)</span>
        <iframe
          title="HTML preview"
          sandbox=""
          srcDoc={input}
          className="min-h-[340px] w-full rounded-lg border border-app bg-white"
        />
      </div>
    </div>
  );
}

/* ── Keycode inspector ────────────────────────────────────────────────────── */

export function KeycodeInspector() {
  const [last, setLast] = useState<{ key: string; code: string; keyCode: number; mods: string } | null>(null);
  return (
    <div className="space-y-4">
      <div
        tabIndex={0}
        onKeyDown={(e) => {
          e.preventDefault();
          const mods = [e.ctrlKey && "Ctrl", e.metaKey && "Cmd", e.altKey && "Alt", e.shiftKey && "Shift"]
            .filter(Boolean)
            .join(" + ");
          setLast({ key: e.key === " " ? "Space" : e.key, code: e.code, keyCode: e.keyCode, mods });
        }}
        className="grid h-40 cursor-pointer place-items-center rounded-2xl border-2 border-dashed border-app bg-surface-2 text-sm text-app-2 outline-none transition focus:border-app-strong focus:text-app"
      >
        {last ? (
          <div className="text-center">
            <div className="text-3xl font-semibold text-app">{last.key}</div>
            <div className="mt-1 font-mono text-xs text-app-2">
              code: {last.code} · keyCode: {last.keyCode}
              {last.mods ? ` · ${last.mods}` : ""}
            </div>
          </div>
        ) : (
          "Click here, then press any key"
        )}
      </div>
      {last && (
        <Card className="divide-y divide-app/40">
          {[
            ["event.key", last.key],
            ["event.code", last.code],
            ["event.keyCode (deprecated)", String(last.keyCode)],
            ["Modifiers", last.mods || "none"],
          ].map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-[11px] uppercase tracking-wider text-app-2">{k}</span>
              <span className="select-all font-mono text-xs font-medium text-app">{v}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ── Hash & HMAC (async WebCrypto) ────────────────────────────────────────── */

export function HashTool({ algo }: { algo: "SHA-1" | "SHA-256" | "SHA-384" | "SHA-512" }) {
  const [input, setInput] = useState("");
  const [out, setOut] = useState("");
  useEffect(() => {
    let alive = true;
    if (!input) {
      setOut("");
      return;
    }
    digest(algo, input).then((h) => {
      if (alive) setOut(h);
    });
    return () => {
      alive = false;
    };
  }, [input, algo]);
  return (
    <div className="space-y-4">
      <label className="block">
        <span className={labelCls}>Input</span>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={`Text to hash with ${algo}…`}
          className={textareaCls}
          spellCheck={false}
        />
      </label>
      <OutputPane text={out} minHeight={64} />
    </div>
  );
}

export function HmacTool() {
  const [algo, setAlgo] = useState("SHA-256");
  const [key, setKey] = useState("");
  const [msg, setMsg] = useState("");
  const [out, setOut] = useState("");
  useEffect(() => {
    let alive = true;
    if (!key || !msg) {
      setOut("");
      return;
    }
    hmac(algo, key, msg).then((h) => {
      if (alive) setOut(h);
    });
    return () => {
      alive = false;
    };
  }, [algo, key, msg]);
  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <label className="block w-36">
          <span className={labelCls}>Algorithm</span>
          <select value={algo} onChange={(e) => setAlgo(e.target.value)} className={inputCls}>
            {["SHA-1", "SHA-256", "SHA-384", "SHA-512"].map((a) => (
              <option key={a}>{a}</option>
            ))}
          </select>
        </label>
        <label className="block flex-1">
          <span className={labelCls}>Secret key</span>
          <input value={key} onChange={(e) => setKey(e.target.value)} className={cn(inputCls, "font-mono")} />
        </label>
      </div>
      <label className="block">
        <span className={labelCls}>Message</span>
        <textarea value={msg} onChange={(e) => setMsg(e.target.value)} className={textareaCls} spellCheck={false} />
      </label>
      <OutputPane text={out} minHeight={64} />
    </div>
  );
}

/* ── Timestamp converter (live) ───────────────────────────────────────────── */

export function TimestampTool() {
  const [now, setNow] = useState(() => new Date());
  const [input, setInput] = useState("");
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const parsed = useMemo(() => {
    const s = input.trim();
    if (!s) return { date: null as Date | null, error: null as string | null };
    if (/^\d{10}$/.test(s)) return { date: new Date(Number(s) * 1000), error: null };
    if (/^\d{13}$/.test(s)) return { date: new Date(Number(s)), error: null };
    const d = new Date(s);
    if (isNaN(d.getTime()))
      return { date: null, error: "Enter a unix timestamp (s or ms) or an ISO-8601 date." };
    return { date: d, error: null };
  }, [input]);

  const rows = (d: Date): [string, string][] => [
    ["Unix (seconds)", String(Math.floor(d.getTime() / 1000))],
    ["Unix (millis)", String(d.getTime())],
    ["ISO 8601 (UTC)", d.toISOString()],
    ["Local", d.toLocaleString()],
    ["UTC", d.toUTCString()],
    ["Relative", relativeTime(d, now)],
  ];

  return (
    <div className="space-y-4">
      <Card className="flex items-center justify-between px-4 py-3">
        <span className="text-[11px] uppercase tracking-wider text-app-2">Now</span>
        <span className="font-mono text-xs text-app">
          {Math.floor(now.getTime() / 1000)} · {now.toISOString()}
        </span>
        <CopyButton text={String(Math.floor(now.getTime() / 1000))} />
      </Card>
      <label className="block">
        <span className={labelCls}>Timestamp or date</span>
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="1720000000 · 2026-07-13T12:00:00Z · Jul 13 2026"
          className={cn(inputCls, "font-mono")}
          spellCheck={false}
        />
      </label>
      {parsed.error && <div className="font-mono text-xs text-rose-400">{parsed.error}</div>}
      {parsed.date && (
        <Card className="divide-y divide-app/40">
          {rows(parsed.date).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between px-4 py-2.5">
              <span className="text-[11px] uppercase tracking-wider text-app-2">{k}</span>
              <span className="select-all font-mono text-xs font-medium text-app">{v}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

/* ── Cron explainer ───────────────────────────────────────────────────────── */

export function CronExplainer() {
  const [expr, setExpr] = useState("*/15 9-17 * * 1-5");
  const result = useMemo(() => {
    if (!expr.trim()) return { desc: "", runs: [] as Date[], error: null as string | null };
    try {
      return { desc: describeCron(expr), runs: nextCronRuns(expr, 5), error: null };
    } catch (e) {
      return { desc: "", runs: [], error: (e as Error).message };
    }
  }, [expr]);

  return (
    <div className="space-y-4">
      <label className="block">
        <span className={labelCls}>Cron expression (minute hour day month weekday)</span>
        <input
          value={expr}
          onChange={(e) => setExpr(e.target.value)}
          className={cn(inputCls, "font-mono text-sm")}
          spellCheck={false}
        />
      </label>
      <div className="flex flex-wrap gap-1.5">
        {["*/5 * * * *", "0 9 * * 1-5", "0 0 * * 0", "30 2 1 * *", "@daily"].map((p) => (
          <button
            key={p}
            onClick={() => setExpr(p)}
            className="rounded-full border border-app bg-surface-2 px-2.5 py-1 font-mono text-[11px] text-app-1 transition hover:border-app-strong"
          >
            {p}
          </button>
        ))}
      </div>
      {result.error ? (
        <Card className="p-3">
          <div className="font-mono text-xs text-rose-400">{result.error}</div>
        </Card>
      ) : (
        result.desc && (
          <>
            <Card className="px-4 py-3">
              <div className="text-[11px] uppercase tracking-wider text-app-2">Meaning</div>
              <div className="mt-1 text-sm font-medium text-app">{result.desc}</div>
            </Card>
            <Card className="divide-y divide-app/40">
              <div className="px-4 py-2 text-[11px] uppercase tracking-wider text-app-2">
                Next 5 runs (local time)
              </div>
              {result.runs.map((d, i) => (
                <div key={i} className="flex items-center justify-between px-4 py-2">
                  <span className="font-mono text-xs text-app">{d.toLocaleString()}</span>
                  <span className="text-[11px] text-app-2">{relativeTime(d, new Date())}</span>
                </div>
              ))}
            </Card>
          </>
        )
      )}
    </div>
  );
}

/* ── UUID / random regenerating list ──────────────────────────────────────── */

export function RegenList({
  make,
  countDefault = 5,
  mono = true,
}: {
  make: () => string;
  countDefault?: number;
  mono?: boolean;
}) {
  const [count, setCount] = useState(countDefault);
  const [items, setItems] = useState<string[]>(() =>
    Array.from({ length: countDefault }, make)
  );
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <label className="block">
          <span className={labelCls}>Count</span>
          <input
            type="number"
            min={1}
            max={100}
            value={count}
            onChange={(e) => setCount(Math.max(1, Math.min(100, Number(e.target.value) || 1)))}
            className={cn(inputCls, "w-24")}
          />
        </label>
        <button
          onClick={() => setItems(Array.from({ length: count }, make))}
          className="flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-xs font-semibold text-black transition hover:brightness-105"
        >
          <RefreshCw size={13} /> Regenerate
        </button>
        <CopyButton text={items.join("\n")} />
      </div>
      <Card className="divide-y divide-app/40">
        {items.map((it, i) => (
          <div key={i} className={cn("select-all px-4 py-2 text-xs text-app", mono && "font-mono")}>
            {it}
          </div>
        ))}
      </Card>
    </div>
  );
}
